<script>
import api from '@utils/api'

export default {
  name: 'OrganizationNew',
  props: {
    newItem: {
      type: Boolean,
      default: false,
    },
    constProps: {
      type: Object,
      default: null,
    },
    modalTitle: {
      type: String,
      default: null,
    },
    organizationInitialType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      valid: true,
      item: {
        shortName: null,
        fullName: null,
        status: 1,
        description: null,
        type: null,
        parent: null,
        city: null,
        region: null,
        customer: null,
        deliveryType: null,
        postcode: null,
      },
      rules: {
        shortName: [x => !!x || this.$t('validation__is_required')],
        fullName: [x => !!x || this.$t('validation__is_required')],
        type: [x => !!x || this.$t('validation__is_required')],
        city: [x => !!x || this.$t('validation__is_required')],
        region: [x => !!x || this.$t('validation__is_required')],
        customer: [x => !!x || this.$t('validation__is_required')],
        deliveryType: [x => !!x || this.$t('validation__is_required')],
      },
      organisations: [],
    }
  },
  computed: {
    type() {
      return this.$store.getters['data/getDictionary']('organizationtype').map(
        item => {
          item.translatedType = this.$t(`organizationtype.${item.code}.name`)
          return item
        }
      )
    },
    city() {
      return this.$store.getters['data/getDictionary']('city')
    },
    region() {
      return this.$store.getters['data/getDictionary']('region')
    },
    deliveryType() {
      return this.$store.getters['data/getDictionary']('deliverytype').map(
        item => {
          item.translatedDeliveryType = this.$t(
            `deliverytype.${item.code}.name`
          )
          return item
        }
      )
    },
    allowedParentType() {
      switch (this.item.type) {
        case 5:
          return 4
        case 1:
        case 2:
        case 3:
          return 5
        default:
          return null
      }
    },
    typeValue() {
      return this.item.type
    },
  },
  watch: {
    typeValue(value) {
      if (value === 4) this.item.parent = null
      this.getOrganizationsForParent()
    },
  },
  mounted() {
    if (this.constProps) {
      this.item = { ...this.item, ...this.constProps }
    }
  },
  methods: {
    closeItem() {
      this.$refs.newForm.reset()
      this.item.status = 1
      this.$emit('close-modal')
    },
    createItem() {
      let urlPath = '/dictionaries/organizations'

      if (this.organizationInitialType) {
        urlPath = this.organizationInitialType
      }

      if (this.$refs.newForm.validate()) {
        this.loading = true
        api
          .createOrganization(this.item)
          .then(response => {
            this.$store.dispatch('data/updateDictionary', 'organization')
            this.$router.push(`${urlPath}/${response.id}`)
          })
          .catch(() => {
            this.$store.commit('notifications/ADD', {
              type: 'error',
              message: this.$t('error__customer_number_already_exists'),
            })
          })
          .then(() => {
            this.loading = false
          })
      }
    },
    getOrganizationsForParent() {
      let params = {
        size: 10000,
        page: 1,
        sort_property: null,
        sort_direction: null,
        filter_type_comparison: 'EQ',
        'filter_type_value[]': this.allowedParentType,
      }
      api.getOrganizations(params).then(response => {
        this.organisations = response.content
      })
    },
  },
}
</script>

<template>
  <v-dialog
    v-model="newItem"
    persistent
    max-width="500px"
  >
    <v-card>
      <v-card-title>
        <v-spacer />
        <div class="headline">{{ modalTitle ? modalTitle : $t('titles__add_organization') }}</div>
        <v-spacer />
      </v-card-title>
      <v-card-text>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex
              xs12
              class="text-xs-center"
            >
              <v-form
                ref="newForm"
                v-model="valid"
                lazy-validation
              >
                <v-text-field
                  :label="$t('label__short_name')"
                  v-model="item.shortName"
                  :rules="rules.shortName"
                  name="shortName"
                  autocomplete="off"
                  required
                />
                <v-text-field
                  :label="$t('label__full_name')"
                  v-model="item.fullName"
                  :rules="rules.fullName"
                  name="completeName"
                  autocomplete="off"
                  required
                />
                <v-text-field
                  :label="$t('label__description')"
                  v-model="item.description"
                  name="description"
                  autocomplete="off"
                />
                <v-select
                  :items="type"
                  :label="$t('label__type')"
                  :rules="rules.type"
                  :disabled="constProps && !!constProps.type"
                  v-model="item.type"
                  item-text="translatedType"
                  item-value="code"
                  autocomplete
                  required
                />
                <v-select
                  :items="organisations"
                  :label="$t('label__organization_parent')"
                  v-model="item.parent"
                  :disabled="!allowedParentType"
                  item-text="shortName"
                  item-value="id"
                  autocomplete
                  clearable
                />
                <v-select
                  :items="city"
                  :label="$t('label__city')"
                  :rules="rules.city"
                  v-model="item.city"
                  item-text="name"
                  item-value="code"
                  autocomplete
                  required
                />
                <v-select
                  :items="region"
                  :label="$t('label__region')"
                  :rules="rules.region"
                  v-model="item.region"
                  item-text="name"
                  item-value="code"
                  autocomplete
                  required
                />
                <v-text-field
                  :label="$t('label__postcode')"
                  v-model="item.postcode"
                  name="postcode"
                  autocomplete="off"
                />
                <v-text-field
                  :label="$t('label__customer')"
                  v-model="item.customer"
                  :rules="rules.customer"
                  name="customer"
                  autocomplete="off"
                  required
                />
                <v-text-field
                  :label="$t('label__securityStock')"
                  v-model="item.securityStock"
                  name="securityStock"
                  autocomplete="off"
                  type="Number"
                />
                <v-select
                  :items="deliveryType"
                  :label="$t('label__delivery_type')"
                  v-model="item.deliveryType"
                  :rules="rules.deliveryType"
                  item-text="translatedDeliveryType"
                  item-value="code"
                  autocomplete
                  required
                />
              </v-form>
            </v-flex>
            <small>* {{ $t('label__requier_small_text') }}</small>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          :disabled="loading"
          color="secondary"
          @click="closeItem">{{ $t('buttons__close') }}</v-btn>
        <v-btn
          :disabled="loading"
          :loading="loading"
          color="primary"
          @click="createItem">{{ $t('buttons__create') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" module>
@import '@design';
.btnWrapper {
  margin-top: 26px;
  margin-left: -8px;
}
</style>
